import React, { useState, useEffect } from 'react';
import { 
  FiDownload, FiHeadphones, FiBell, FiSettings, FiSun, FiMoreHorizontal,
  FiTrendingUp, FiTrendingDown, FiDollarSign, FiBriefcase, FiActivity, FiUsers, FiClock,
  FiChevronRight, FiX
} from 'react-icons/fi';
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, 
  BarChart, Bar, LineChart, Line, Cell
} from 'recharts';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function DashboardPage() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d'); 

  // 🌓 ตรวจจับการเปลี่ยนธีมแบบ Real-time เพื่ออัปเดตกราฟและสไตล์ Recharts
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // 📅 States สำหรับปฏิทินนัดหมายแบบตอบโต้ (Interactive Calendar)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCalendarJobs, setSelectedCalendarJobs] = useState([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState('');
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // ชื่อเดือนภาษาไทย
  const monthNamesTH = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
  ];

  // วันในสัปดาห์
  const dayNamesTH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

  // คำนวณวันในเดือน
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDayOfWeek = new Date(year, month, 1).getDay();

  // นำทางเดือน
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // วาดช่องปฏิทินในแต่ละวัน
  const renderCalendarCells = () => {
    const cells = [];
    
    // เติมช่องว่างของสัปดาห์แรก
    for (let i = 0; i < startDayOfWeek; i++) {
      cells.push(<div key={`empty-${i}`} className="h-10 sm:h-12"></div>);
    }

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // วาดแต่ละวัน
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayJobs = jobs.filter(j => j.appointmentDate === dateStr);
      
      const isToday = dateStr === todayStr;
      const hasJobs = dayJobs.length > 0;

      cells.push(
        <button
          key={`day-${d}`}
          onClick={() => {
            if (hasJobs) {
              setSelectedCalendarJobs(dayJobs);
              setSelectedCalendarDate(dateStr);
              setIsCalendarModalOpen(true);
            } else {
              Swal.fire({
                title: `วันที่ ${d} ${monthNamesTH[month]} ${year + 543}`,
                text: 'ไม่มีคิวงานนัดหมายในวันนี้ครับ',
                icon: 'info',
                confirmButtonText: 'รับทราบ'
              });
            }
          }}
          className={`h-10 sm:h-12 w-full flex flex-col items-center justify-center rounded-xl relative transition-all duration-300 hover:scale-105 active:scale-95 ${
            isToday 
              ? 'bg-indigo-600 text-white font-extrabold shadow-lg shadow-indigo-600/30' 
              : hasJobs
                ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/40 font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900/60'
                : 'bg-gray-100 dark:bg-[#1b2132]/30 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1e2330]'
          }`}
        >
          <span className="text-xs sm:text-sm">{d}</span>
          {hasJobs && (
            <span className="absolute bottom-1 w-4 h-4 bg-indigo-50 text-indigo-600 dark:text-indigo-400 font-extrabold text-[8px] rounded-full flex items-center justify-center shadow-sm">
              {dayJobs.length}
            </span>
          )}
        </button>
      );
    }

    return cells;
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_all_jobs`);
      const data = await res.json();
      if (data.success && data.data) {
        setJobs(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // 🧮 1. ฟิลเตอร์ข้อมูลตามช่วงเวลา
  const filteredJobs = jobs.filter(job => {
    if (timeRange === 'all') return true;
    const jobDate = new Date(job.createdAt);
    const now = new Date();
    const daysDiff = (now - jobDate) / (1000 * 60 * 60 * 24);
    if (timeRange === '7d') return daysDiff <= 7;
    if (timeRange === '30d') return daysDiff <= 30;
    if (timeRange === '90d') return daysDiff <= 90;
    return true;
  });

  // 🧮 2. คำนวณสถิติ Card ด้านบน
  const completedJobs = filteredJobs.filter(j => j.status === 'completed');
  const totalRevenue = completedJobs.reduce((sum, j) => sum + Number(j.price || 0), 0);
  const totalJobsCount = filteredJobs.length;
  const completionRate = totalJobsCount > 0 ? ((completedJobs.length / totalJobsCount) * 100).toFixed(1) : 0;
  const activeJobsCount = filteredJobs.filter(j => ['pending_approval','pending','traveling','in_progress'].includes(j.status)).length;
  const uniqueCustomers = new Set(filteredJobs.map(j => j.phone)).size;

  // 📊 3. เตรียมข้อมูลกราฟ Timeline
  const timelineDataMap = filteredJobs.reduce((acc, job) => {
    const date = job.createdAt ? job.createdAt.split(' ')[0] : 'Unknown';
    if (!acc[date]) acc[date] = { name: date.substring(5), revenue: 0, jobs: 0 };
    if (job.status === 'completed') acc[date].revenue += Number(job.price || 0);
    acc[date].jobs += 1;
    return acc;
  }, {});
  const timelineData = Object.values(timelineDataMap).sort((a,b) => a.name.localeCompare(b.name)).slice(-15); 

  // 📊 4. เตรียมข้อมูลเปรียบเทียบประเภทงาน
  const categoryCount = filteredJobs.reduce((acc, job) => {
    acc[job.job_type] = (acc[job.job_type] || 0) + 1;
    return acc;
  }, {});
  
  const getJobTypeLabel = (type) => {
    const types = { 'aircon': 'แอร์', 'electric': 'ไฟฟ้า', 'contractor': 'รับเหมา', 'solar': 'โซล่าเซลล์', 'door': 'ประตูอัตโนมัติ', 'evertor': 'ลิฟท์', 'camera': 'กล้องวงจรปิด', 'rework': 'งานแก้' };
    return types[type] || 'ทั่วไป';
  };

  const categoryData = Object.keys(categoryCount).map(key => ({
    name: getJobTypeLabel(key),
    count: categoryCount[key],
    originalKey: key
  })).sort((a, b) => b.count - a.count); 

  const barColors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'];

  // 🏆 5. ข้อมูลงานล่าสุด
  const recentTopJobs = completedJobs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1e2330] border border-[#334155] p-3 rounded-lg shadow-xl">
          <p className="text-gray-400 text-xs mb-1">{label}</p>
          <p className="text-white font-bold text-sm">
            {payload[0].name === 'revenue' ? '฿' : ''}{Number(payload[0].value).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-gray-800 dark:text-gray-100 font-sans">
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-850 dark:text-white transition-colors">📊 แผงควบคุมระบบ (Dashboard)</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">ติดตามข้อมูลรายรับ สถิติงาน และตารางนัดหมายบริการลูกค้า</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-lg p-1 shadow-sm">
            <button onClick={() => setTimeRange('7d')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${timeRange === '7d' ? 'bg-indigo-50 dark:bg-[#2d1b69] text-indigo-600 dark:text-[#c4b5fd]' : 'text-gray-550 hover:text-gray-800 dark:hover:text-gray-300'}`}>7d</button>
            <button onClick={() => setTimeRange('30d')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${timeRange === '30d' ? 'bg-indigo-50 dark:bg-[#2d1b69] text-indigo-600 dark:text-[#c4b5fd]' : 'text-gray-550 hover:text-gray-800 dark:hover:text-gray-300'}`}>30d</button>
            <button onClick={() => setTimeRange('90d')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${timeRange === '90d' ? 'bg-indigo-50 dark:bg-[#2d1b69] text-indigo-600 dark:text-[#c4b5fd]' : 'text-gray-550 hover:text-gray-800 dark:hover:text-gray-300'}`}>90d</button>
          </div>
          <button className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] hover:bg-gray-50 dark:hover:bg-[#1e2330] text-gray-605 dark:text-gray-300 p-2 rounded-lg transition-all shadow-sm">
            <FiDownload size={16} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64 text-indigo-500 animate-pulse font-bold">Loading Data...</div>
      ) : (
        <>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm dark:shadow-lg relative overflow-hidden group transition-all">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity"><FiDollarSign size={80}/></div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Total Revenue</p>
                <div className="bg-blue-500/20 text-blue-600 dark:text-blue-400 p-1.5 rounded-md"><FiDollarSign size={12}/></div>
              </div>
              <h3 className="text-2xl font-black text-gray-800 dark:text-white mb-2">{(totalRevenue / 1000).toFixed(1)}K</h3>
              <p className="text-[10px] text-emerald-605 dark:text-emerald-400 font-bold flex items-center gap-1"><FiTrendingUp/> +12.5% <span className="text-gray-400 dark:text-gray-605 ml-1 font-normal">vs last period</span></p>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm dark:shadow-lg relative overflow-hidden group transition-all">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity"><FiBriefcase size={80}/></div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Total Jobs</p>
                <div className="bg-pink-500/20 text-pink-606 dark:text-pink-400 p-1.5 rounded-md"><FiBriefcase size={12}/></div>
              </div>
              <h3 className="text-2xl font-black text-gray-800 dark:text-white mb-2">{totalJobsCount}</h3>
              <p className="text-[10px] text-rose-600 dark:text-rose-400 font-bold flex items-center gap-1"><FiTrendingDown/> -2.4% <span className="text-gray-400 dark:text-gray-605 ml-1 font-normal">vs last period</span></p>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm dark:shadow-lg relative overflow-hidden group transition-all">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity"><FiActivity size={80}/></div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Completion Rate</p>
                <div className="bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 p-1.5 rounded-md"><FiTrendingUp size={12}/></div>
              </div>
              <h3 className="text-2xl font-black text-gray-800 dark:text-white mb-2">{completionRate}%</h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium mt-1">from total requested</p>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm dark:shadow-lg relative overflow-hidden group transition-all">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity"><FiClock size={80}/></div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Active Jobs</p>
                <div className="bg-purple-500/20 text-purple-600 dark:text-purple-400 p-1.5 rounded-md"><FiClock size={12}/></div>
              </div>
              <h3 className="text-2xl font-black text-gray-800 dark:text-white mb-2">{activeJobsCount}</h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium mt-1">currently working/pending</p>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-4 rounded-xl shadow-sm dark:shadow-lg relative overflow-hidden group transition-all">
              <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity"><FiUsers size={80}/></div>
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Customers</p>
                <div className="bg-amber-500/20 text-amber-600 dark:text-amber-400 p-1.5 rounded-md"><FiUsers size={12}/></div>
              </div>
              <h3 className="text-2xl font-black text-gray-850 dark:text-white mb-2">{uniqueCustomers}</h3>
              <p className="text-[10px] text-emerald-650 dark:text-emerald-400 font-bold flex items-center gap-1"><FiTrendingUp/> +15.0% <span className="text-gray-400 dark:text-gray-605 ml-1 font-normal">new clients</span></p>
            </div>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 text-left">
            
            <div className="lg:col-span-2 bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-5 rounded-xl shadow-sm dark:shadow-lg transition-colors">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2"><FiTrendingUp className="text-emerald-500 dark:text-emerald-400"/> Revenue Timeline</h3>
                <FiMoreHorizontal className="text-gray-450 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white cursor-pointer" />
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timelineData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#1e2330" : "#e2e8f0"} />
                    <XAxis dataKey="name" tick={{fontSize: 10, fill: isDark ? '#64748b' : '#475569'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 10, fill: isDark ? '#64748b' : '#475569'}} axisLine={false} tickLine={false} tickFormatter={(val) => `฿${val/1000}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-5 rounded-xl shadow-sm dark:shadow-lg flex flex-col transition-colors">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white">Job Type Comparison</h3>
                <div className="flex gap-2">
                  <span className="text-[9px] bg-gray-100 dark:bg-[#1e2330] px-2 py-1 rounded text-gray-600 dark:text-gray-300">Total Jobs</span>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-4 mt-2">
                  {categoryData.length > 0 ? categoryData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-16 text-[10px] text-gray-500 dark:text-gray-400 truncate text-right">{item.name}</div>
                      <div className="flex-1 h-3 bg-gray-100 dark:bg-[#1e2330] rounded-full overflow-hidden relative">
                        <div 
                          className="h-full rounded-full transition-all duration-1000" 
                          style={{ 
                            width: `${(item.count / totalJobsCount) * 100}%`,
                            backgroundColor: barColors[idx % barColors.length] 
                          }}
                        ></div>
                      </div>
                      <div className="w-8 text-right text-xs font-bold text-gray-850 dark:text-white">{item.count}</div>
                    </div>
                  )) : (
                    <p className="text-center text-gray-400 dark:text-gray-600 text-xs mt-10">No data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 📅 2-Column Grid: ปฏิทินจองคิวนัดหมาย (ซ้าย 2/3) และสถิติสรุปล่วงหน้า (ขวา 1/3) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 text-left">
            
            {/* 📅 ส่วนปฏิทินนัดหมายแบบตอบโต้ได้ (Interactive Calendar) */}
            <div className="lg:col-span-2 bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-5 rounded-xl shadow-sm dark:shadow-lg flex flex-col justify-between transition-colors">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-850 dark:text-white flex items-center gap-2">
                    <span className="text-indigo-600 dark:text-indigo-400">📅</span> ปฏิทินจองคิวและวันนัดหมายลูกค้า
                  </h3>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">เลือกวันที่ไฮไลต์สีเพื่อดูรายละเอียดคิวงานนัดหมายล่วงหน้า</p>
                </div>
                
                {/* ปุ่มเลื่อนเดือน */}
                <div className="flex items-center gap-2">
                  <button 
                    onClick={prevMonth}
                    className="p-1.5 bg-gray-100 dark:bg-[#1b2132] hover:bg-gray-200 dark:hover:bg-[#252c42] rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-850 dark:hover:text-white transition font-mono"
                  >
                    ◀
                  </button>
                  <span className="text-xs font-black text-indigo-650 dark:text-indigo-400 min-w-[100px] text-center">
                    {monthNamesTH[month]} {year + 543}
                  </span>
                  <button 
                    onClick={nextMonth}
                    className="p-1.5 bg-gray-100 dark:bg-[#1b2132] hover:bg-gray-200 dark:hover:bg-[#252c42] rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-850 dark:hover:text-white transition font-mono"
                  >
                    ▶
                  </button>
                </div>
              </div>

              {/* หัวตารางวันในสัปดาห์ */}
              <div className="grid grid-cols-7 gap-2 mb-2 text-center text-[10px] font-bold text-gray-450 dark:text-gray-500">
                {dayNamesTH.map((d, i) => (
                  <div key={i} className={i === 0 || i === 6 ? 'text-rose-500' : ''}>{d}</div>
                ))}
              </div>

              {/* ช่องปฏิทินในแต่ละวัน */}
              <div className="grid grid-cols-7 gap-2">
                {renderCalendarCells()}
              </div>

            </div>

            {/* 📋 รายละเอียดด่วนคิวงานนัดหมายวันนี้ */}
            <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] p-5 rounded-xl shadow-sm dark:shadow-lg flex flex-col justify-between text-left transition-colors">
              <div>
                <h3 className="text-sm font-bold text-gray-850 dark:text-white mb-1 flex items-center gap-1.5">
                  <span className="text-emerald-505 dark:text-emerald-400">📋</span> คิวงานนัดหมายวันนี้
                </h3>
                <p className="text-[10px] text-gray-450 dark:text-gray-500 mb-4">รายการคิวที่มีกำหนดนัดเข้าบริการในวันนี้</p>
                
                <div className="space-y-3 overflow-y-auto max-h-[220px] pr-1 custom-scrollbar">
                  {(() => {
                    const today = new Date();
                    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    const todaysJobs = jobs.filter(j => j.appointmentDate === dateStr);
                    
                    if (todaysJobs.length > 0) {
                      return todaysJobs.map((job) => (
                        <div key={job.id} className="bg-gray-50 dark:bg-[#1b2132]/50 p-2.5 rounded-lg border border-gray-200 dark:border-[#1e2330] text-xs transition-colors">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-bold text-gray-850 dark:text-white truncate max-w-[110px]">{job.customerName}</span>
                            <span className="text-[9px] bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-extrabold px-1.5 py-0.5 rounded">
                              ⏱️ {job.appointmentTime || 'ไม่ระบุเวลา'}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">🔨 อาการ: {job.problem}</p>
                          <div className="flex justify-between items-center mt-2 pt-1.5 border-t border-gray-200 dark:border-[#1e2330]">
                            <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold">฿{Number(job.price || 0).toLocaleString()}</span>
                            <span className="text-[8px] text-gray-405 dark:text-gray-500 truncate max-w-[90px]">👷‍♂️ {job.technicianName || 'ยังไม่มอบหมาย'}</span>
                          </div>
                        </div>
                      ));
                    } else {
                      return (
                        <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-xs italic flex flex-col items-center justify-center gap-2">
                          <span className="text-2xl">💤</span>
                          <span>วันนี้ไม่มีคิวงานนัดหมายบริการครับ</span>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-[#1e2330] mt-4 flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500">
                <span>มีคิวงานรอการอนุมัติ:</span>
                <span className="font-bold text-yellow-600 dark:text-yellow-500">{jobs.filter(j => j.status === 'pending_approval').length} งาน</span>
              </div>
            </div>

          </div>

          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-xl shadow-sm dark:shadow-lg overflow-hidden transition-colors">
            <div className="p-5 border-b border-gray-200 dark:border-[#1e2330] flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-850 dark:text-white flex items-center gap-2">
                <span className="text-yellow-600 dark:text-yellow-500">🏆</span> Recent Completed Jobs <span className="text-[10px] font-normal text-gray-450 dark:text-gray-500">- Your best performing services</span>
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600 dark:text-gray-350">
                <thead className="text-[10px] uppercase bg-gray-50 dark:bg-[#131825] text-gray-500 border-b border-gray-200 dark:border-[#1e2330] transition-colors">
                  <tr>
                    <th className="px-5 py-3 font-medium">Customer / Problem</th>
                    <th className="px-5 py-3 font-medium">Category</th>
                    <th className="px-5 py-3 font-medium text-right">Revenue</th>
                    <th className="px-5 py-3 font-medium text-center">Tech</th>
                    <th className="px-5 py-3 font-medium text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-[#1e2330] transition-colors">
                  {recentTopJobs.length > 0 ? recentTopJobs.map((job, idx) => (
                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-[#1e2330]/50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-[#1e2330] flex items-center justify-center text-[10px] font-bold text-gray-400 dark:text-gray-500">{idx + 1}</span>
                          <div>
                            <p className="font-bold text-gray-800 dark:text-gray-200 truncate max-w-[200px]">{job.customerName}</p>
                            <p className="text-[10px] text-gray-405 dark:text-gray-500 truncate max-w-[200px]">{job.problem}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-[10px] px-2 py-1 rounded bg-gray-150 dark:bg-[#1e2330] text-blue-600 dark:text-blue-400 font-bold border border-gray-200 dark:border-gray-700">
                          {getJobTypeLabel(job.job_type)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right font-black text-emerald-600 dark:text-emerald-400">
                        ฿{Number(job.price).toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-center text-[10px] text-gray-550 dark:text-gray-400">
                        {job.technicianName || '-'}
                      </td>
                      <td className="px-5 py-4 text-right text-[10px] text-gray-400 dark:text-gray-500 flex justify-end items-center gap-1">
                        {job.createdAt.split(' ')[0]} <FiChevronRight size={10}/>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center py-10 text-gray-550 text-xs">No completed jobs found in this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* 📅 MODAL คิวงานนัดหมายในปฏิทิน */}
      {isCalendarModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in text-gray-800">
          <div className="bg-white dark:bg-[#131825] dark:text-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden text-left">
            
            <div className="bg-indigo-600 text-white p-5 flex justify-between items-center border-b border-indigo-700">
              <div>
                <h3 className="font-black text-sm sm:text-base flex items-center gap-2">
                  📅 คิวงานนัดหมายวันที่ {new Date(selectedCalendarDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <p className="text-[10px] text-indigo-200 mt-0.5 font-bold">มีรายการจองคิวทั้งหมด {selectedCalendarJobs.length} รายการ</p>
              </div>
              <button 
                onClick={() => setIsCalendarModalOpen(false)} 
                className="bg-indigo-700/50 hover:bg-indigo-800 text-white transition p-2 rounded-full"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
              {selectedCalendarJobs.map((job, idx) => (
                <div 
                  key={job.id} 
                  className="bg-gray-50 dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] flex flex-col md:flex-row justify-between items-start md:items-center gap-3 transition hover:shadow-md"
                >
                  <div className="space-y-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 text-xs font-black flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <p className="font-bold text-gray-900 dark:text-white">{job.customerName}</p>
                      <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-extrabold px-2 py-0.5 rounded-full">
                        ⏱️ {job.appointmentTime || 'ไม่ระบุเวลา'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium pl-7">📞 เบอร์โทร: {job.phone}</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 font-bold pl-7 mt-1">🔧 อาการเสีย/ปัญหา: {job.problem}</p>
                    {job.technicianName && (
                      <p className="text-[10px] text-gray-400 font-medium pl-7 mt-0.5">👷‍♂️ ช่างผู้ดูแล: {job.technicianName}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0 w-full md:w-auto mt-2 md:mt-0 pl-7 md:pl-0 border-t md:border-t-0 pt-2 md:pt-0 border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-black text-emerald-500">฿{Number(job.price || 0).toLocaleString()}</p>
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full mt-1.5 ${
                      job.status === 'completed' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : job.status === 'cancelled'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {job.status === 'completed' ? 'เสร็จสิ้น' : job.status === 'cancelled' ? 'ยกเลิก' : 'กำลังดำเนินการ'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 dark:bg-[#1a2130] p-4 flex justify-end border-t border-gray-200 dark:border-[#334155]">
              <button 
                type="button" 
                onClick={() => setIsCalendarModalOpen(false)}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs transition"
              >
                ปิดหน้าต่าง
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}